/**
 * Goal seek: a manual value typed into a formula cell is turned into the value ANOTHER column
 * of that row must hold for the formula to produce it — and the formula itself stays untouched.
 *
 * This is the missing half of `inverse.ts`, which has always been able to rearrange `y = f(x)`
 * into `x = f⁻¹(y)` but was never connected to anything (docs/Grid/FORMULAS.md, "Bidirectional
 * formulas"). The design pass that was owed is answered here by a deliberate narrowing: the
 * direction is NOT decided per row by whichever cell was edited last — it is decided once, per
 * formula column, by whoever writes the formula (`overrideTargets` in the config). So there is
 * still exactly one formula per field and the dependency graph stays acyclic; what changes is
 * only where a typed value lands.
 *
 * `[@Quantity]*[@Price (USD)]` in `Value (USD)`, pointed at `Quantity`: typing `500` into
 * `Value (USD)` writes `500/[@Price (USD)]` into `Quantity` as an ordinary cell value, and
 * `Value (USD)` goes on computing.
 *
 * The typed number is substituted into the inverse as a LITERAL rather than left as a reference
 * to the formula column, which is what makes the result computable: the cell the user just typed
 * into never stores that number, so nothing in the grid could read it back.
 */

import { conditionIndexes, inlineLets, invertFormula } from './inverse'
import { formulaReferences, type AstNode } from './language'
import { fromStored, toStored, type StoredFormula } from './stored'

/** Sentinel for "leave the typed value in the cell", the behaviour that predates this module. */
export const SAME_COLUMN = ''

/**
 * A reference is a goal-seek candidate only when it names a cell of the SAME row of the SAME
 * sheet: an absolute cell (`row: {id}`), a whole-column aggregate (`row: 'column'`) and anything
 * in another sheet or document all describe values that are not this row's to rewrite.
 */
const isLocalCurrentRow = (reference: { row: unknown; sheetId?: string; documentId?: string }): boolean =>
  reference.row === 'current' && reference.sheetId === undefined && reference.documentId === undefined

/**
 * The fields `formula` can be back-solved for, in the order they appear. Only the ones
 * `invertFormula` actually accepts are listed: the dropdown then offers no choice that would
 * fail at typing time, which is the only honest way to present it.
 */
export function goalSeekCandidates(formula: StoredFormula, resultField: string): string[] {
  const ast = inlineLets(fromStored(formula))
  const fields: string[] = []
  for (const reference of formulaReferences(ast)) {
    if (!isLocalCurrentRow(reference)) continue
    if (reference.field === resultField || fields.includes(reference.field)) continue
    // Which branch a row takes is only known per row; any literal stands in for it here, since
    // all that matters is whether the branches can be solved.
    const frozen = freezeConditions(ast, reference.field, 0)
    if (invertFormula(frozen, reference.field, resultField).ok) fields.push(reference.field)
  }
  return fields
}

export type GoalSeekResult =
  | { ok: true; formula: StoredFormula }
  | { ok: false; reason: string }

/**
 * The formula to evaluate in `targetField` so that `resultField` computes `value`, with `value`
 * already substituted in. `current` is what `targetField` holds in the row now: an `IF`/`IFS`
 * condition that reads `targetField` is decided with it, so the row keeps the branch it is on.
 * Evaluating the result is the caller's job — `cellRuntime.validateCandidate`
 * does it for the right row and reports a cycle if the pair happens to feed each other.
 */
export function goalSeekFormula(
  formula: StoredFormula,
  resultField: string,
  targetField: string,
  value: number,
  current: unknown = null,
): GoalSeekResult {
  const ast = freezeConditions(inlineLets(fromStored(formula)), targetField, current)
  const inverted = invertFormula(ast, targetField, resultField)
  if (!inverted.ok) return inverted
  return { ok: true, formula: toStored(substitute(inverted.ast, resultField, value)) }
}

/**
 * Every reference to `field` in this row replaced by the number typed.
 *
 * A reference inside a `range` is left alone on purpose: a range's endpoints are refs by
 * grammar, and a literal there would not be a range any more. Such a formula keeps referring to
 * `resultField` and the runtime reports the cycle — which is the truth about it.
 */
function substitute(node: AstNode, field: string, value: number): AstNode {
  return replaceRefs(node, field, (ref) => ({ kind: 'number', n: value, text: String(value), start: ref.start, end: ref.end }))
}

/** Every same-row reference to `field` in `node` swapped for what `replacement` builds. */
function replaceRefs(node: AstNode, field: string, replacement: (ref: Extract<AstNode, { kind: 'ref' }>) => AstNode): AstNode {
  switch (node.kind) {
    case 'ref':
      return node.field === field && isLocalCurrentRow({ row: node.row ?? 'current', ...(node.sheetId === undefined ? {} : { sheetId: node.sheetId }), ...(node.documentId === undefined ? {} : { documentId: node.documentId }) })
        ? replacement(node)
        : node
    case 'number':
    case 'text':
    case 'raw':
    case 'range':
    case 'name':
      return node
    case 'call':
      return { ...node, args: node.args.map((arg) => replaceRefs(arg, field, replacement)) }
    case 'unary':
    case 'percent':
    case 'group':
      return { ...node, arg: replaceRefs(node.arg, field, replacement) }
    case 'binary':
      return { ...node, left: replaceRefs(node.left, field, replacement), right: replaceRefs(node.right, field, replacement) }
  }
}

/** The literal a row value is frozen to. A blank becomes `""`, which ISNUMBER and `<>""` read as
 *  blank does; booleans become 1 and 0, as everywhere else in this grammar. */
function literalOf(value: unknown, span: { start: number; end: number }): AstNode {
  if (typeof value === 'number') return { kind: 'number', n: value, text: String(value), ...span }
  if (typeof value === 'boolean') return { kind: 'number', n: value ? 1 : 0, text: value ? '1' : '0', ...span }
  return { kind: 'text', s: typeof value === 'string' ? value : '', ...span }
}

/**
 * Every condition of an `IF`/`IFS` with this row's references to `field` replaced by `current`.
 * The inverse is evaluated AT `field`'s cell, so a condition still reading it would be a cycle;
 * and choosing the branch by the value the row holds now is the honest reading of "the branch
 * this row uses". Results are left alone — those are what gets solved.
 */
function freezeConditions(node: AstNode, field: string, current: unknown): AstNode {
  switch (node.kind) {
    case 'number':
    case 'text':
    case 'raw':
    case 'range':
    case 'name':
    case 'ref':
      return node
    case 'call': {
      const conditions = conditionIndexes(node)
      return {
        ...node,
        args: node.args.map((arg, i) => conditions.includes(i)
          ? replaceRefs(arg, field, (ref) => literalOf(current, { start: ref.start, end: ref.end }))
          : freezeConditions(arg, field, current)),
      }
    }
    case 'unary':
    case 'percent':
    case 'group':
      return { ...node, arg: freezeConditions(node.arg, field, current) }
    case 'binary':
      return { ...node, left: freezeConditions(node.left, field, current), right: freezeConditions(node.right, field, current) }
  }
}
